import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, rupiah, STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { InlineAuth } from "@/components/InlineAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { PackageSearch, Star, MessageSquareWarning, MapPin } from "lucide-react";

const DONE = ["selesai"];
const CANCEL = ["dibatalkan_penumpang", "dibatalkan_driver", "dibatalkan_admin", "kadaluarsa", "pengembalian_dana"];
const statusColor = (s) => (DONE.includes(s) ? "bg-emerald-500/15 text-emerald-400" : CANCEL.includes(s) ? "bg-red-500/15 text-red-400" : "bg-primary/15 text-primary");
const fmtDate = (d) => (d ? String(d).split("-").reverse().join("/") : "-");

const OrderRow = ({ o }) => (
  <div className="border border-border bg-card p-5" data-testid={`my-order-${o.order_number}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="font-mono-terminal text-lg font-bold text-primary">{o.order_number}</span>
      <Badge className={`${statusColor(o.status)} hover:${statusColor(o.status)}`}>{STATUS_LABELS[o.status] || o.status}</Badge>
    </div>
    <div className="mt-3 grid gap-1 font-mono-terminal text-sm">
      <div className="flex justify-between"><span className="text-muted-foreground">Rute</span><span className="text-right">{o.origin} → {o.destination}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Jadwal</span><span>{fmtDate(o.date)} · {o.time}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Layanan</span><span>{o.service_label || o.service_type}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Pax / Total</span><span>{o.pax} · {rupiah(o.total)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Driver/Travel</span><span className="text-right">{o.driver_name || "-"}</span></div>
    </div>
    <div className="mt-4 flex flex-wrap gap-2 text-sm">
      <Link to={`/lacak-order/${o.order_number}`}>
        <Button size="sm" variant="outline" data-testid={`my-order-track-${o.order_number}`}><MapPin className="mr-1 h-3.5 w-3.5" /> Lacak</Button>
      </Link>
      {o.status === "selesai" && !o.rated && (
        <Link to={`/ulasan/${o.order_number}`}>
          <Button size="sm" className="bg-primary text-primary-foreground" data-testid={`my-order-review-${o.order_number}`}><Star className="mr-1 h-3.5 w-3.5" /> Beri Ulasan</Button>
        </Link>
      )}
      <Link to={`/pengaduan/${o.order_number}`}>
        <Button size="sm" variant="ghost" className="text-muted-foreground" data-testid={`my-order-complaint-${o.order_number}`}><MessageSquareWarning className="mr-1 h-3.5 w-3.5" /> Pengaduan</Button>
      </Link>
    </div>
  </div>
);

export default function MyOrders() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState(null);

  const load = () => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  return (
    <div className="min-h-screen bg-background">
      <Seo title="Pesanan Saya | Travele" description="Riwayat pesanan travel Anda di Travele." noindex />
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <h1 className="font-display text-3xl font-black uppercase tracking-tighter" data-testid="my-orders-title">Pesanan Saya</h1>
        <p className="mt-2 text-muted-foreground">Riwayat pemesanan travel Anda beserta statusnya.</p>

        {!loading && !user && (
          <div className="mt-6 border border-border bg-card p-6" data-testid="my-orders-auth-gate">
            <p className="mb-4 text-sm text-muted-foreground">Masuk sebagai member untuk melihat riwayat pesanan Anda.</p>
            <InlineAuth onAuthed={load} />
          </div>
        )}

        {user && orders === null && <p className="mt-8 text-muted-foreground">Memuat...</p>}

        {user && orders !== null && orders.length === 0 && (
          <div className="mt-8 border border-border bg-card p-10 text-center" data-testid="my-orders-empty">
            <PackageSearch className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">Anda belum memiliki pesanan.</p>
            <Link to="/#cari"><Button className="mt-4 bg-primary font-bold text-primary-foreground" data-testid="my-orders-search-btn">Cari Jadwal Sekarang</Button></Link>
          </div>
        )}

        {user && orders !== null && orders.length > 0 && (
          <div className="mt-6 space-y-4" data-testid="my-orders-list">
            {orders.map((o) => <OrderRow key={o.id} o={o} />)}
          </div>
        )}
      </div>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
